<template>
  <Layout>
    <div class="container" v-if="event">
      <div class="reports-top">
        <div class="title">
          Reports For {{ event.conference_name
          }}{{ selectedReport ? " - " + selectedReport.title : "" }}
        </div>
        <div class="actions">
          <RouterLink
            style="text-decoration: none"
            :to="{
              name: 'school-edit-events'
            }"
          >
            <div class="action">
              <v-icon class="icon" color="primary">mdi-calendar-edit</v-icon>
              <span class="text">Edit</span>
            </div>
          </RouterLink>
          <RouterLink
            style="text-decoration: none"
            :to="{
              name: 'school-edit-schedule'
            }"
          >
            <div class="action">
              <v-icon class="icon" color="primary">mdi-calendar-plus</v-icon>
              <span class="text">Schedule</span>
              <v-tooltip activator="parent" location="bottom">
                Add/remove teachers and teams from the event and view/edit their schedule
              </v-tooltip>
            </div>
          </RouterLink>
        </div>
      </div>
      <div style="width: 300px; max-width: 100%">
        <template v-if="selectedReport?.value === 'teacher'">
          <p class="text-primary mb-1 mt-2"><strong>Please select a teacher to generate the report:</strong></p>
          <Autocomplete
            label="Teachers"
            bg-color="white"
            density="compact"
            :items="teachers"
            :loading="teachersS.teachers.loading"
            item-value="value"
            item-title="title"
            v-model="state.teacher"
            clearable
            color="primary"
            :max-items="100"
            data-cy="report-teacher-select"
            :item-props="{
              'data-cy': 'report-teacher-select-item'
            }"
            placeholder="Select a teacher"
          >
          </Autocomplete>
        </template>
        <template v-if="selectedReport?.value === 'team'">
          <p class="text-primary mb-1 mt-2"><strong>Please select a team to generate the report:</strong></p>
          <Autocomplete
            label="Teams"
            bg-color="white"
            density="compact"
            :items="teams"
            :loading="teamsS.teams.loading"
            item-value="value"
            item-title="title"
            v-model="state.team"
            clearable
            color="primary"
            :max-items="100"
            data-cy="report-team-select"
            :item-props="{
              'data-cy': 'report-team-select-item'
            }"
            placeholder="Select a team"
          >
          </Autocomplete>
        </template>
        <template v-if="selectedReport?.value === 'parent'">
          <p class="text-primary mb-1 mt-2"><strong>Please select a parent to generate the report:</strong></p>
          <Autocomplete
            label="Parents"
            bg-color="white"
            density="compact"
            :items="parents"
            :loading="parentsS.parents.loading"
            item-value="value"
            item-title="title"
            v-model="state.parent"
            clearable
            color="primary"
            :max-items="100"
            data-cy="report-parent-select"
            :item-props="{
              'data-cy': 'report-parent-select-item'
            }"
            placeholder="Select a parent"
          >
          </Autocomplete>
        </template>
      </div>
      <!-- Debug info removed for production -->
      
      <ReportTable
        v-if="selectedReport"
        :show="showTable"
        :data="data.table_data"
        :columns="data.table_cols"
        :loading="loading"
        :name="selectedReport!.title"
        :type="selectedReport.value"
        :event-id="eventId"
        :entity-name="getEntityName()"
      ></ReportTable>
      <div v-if="!selectedReport" class="d-flex flex-wrap justify-content-center" style="gap: 20px">
        <template v-for="report in reportTypes" :key="report.value">
          <SelectReportItem :title="report.title" :value="report.value"></SelectReportItem>
        </template>
      </div>
    </div>
    <div
      v-if="(!event && confStore.loading) || (!reportTypes.length && loadingReports)"
      style="width: 100%; margin-top: 200px"
    >
      <LoadingState text="Loading Reports..."></LoadingState>
    </div>
  </Layout>
</template>

<script setup lang="ts">
import { useConferenceStore } from "@/stores/teacher/conference";
import { cloneDeep } from "lodash";
import { watch } from "vue";
import { computed } from "vue";
import Layout from "./Layout.vue";
import LoadingState from "@/components/loadingState.vue";
import { asyncComputed } from "@/utils/asyncComputed";
import FetchService from "@/services/fetch.service";
import { reactive } from "vue";
import type { ReportPayload } from "@/components/school/reports/constants";
import ReportTable from "@/components/school/reports/reportTable.vue";
import SelectReportItem from "@/components/school/reports/selectReportItem.vue";
import router from "@/router";
import useTeachers from "@/stores/school/teachers";
import useTeams from "@/stores/school/teams";
import useParents from "@/stores/school/parents";
import Autocomplete from "@/components/autocomplete.vue";
import { sort } from "@/utils/sort";
import { addEmptyTimeSlot } from "@/components/school/reports/utils";

const props = defineProps<{
  account?: string;
  eventId: string;
}>();

const state = reactive({
  type: (router.currentRoute.value.query.type as string) || "",
  teacher: undefined as string | undefined,
  team: undefined as string | undefined,
  parent: undefined as string | undefined
});

const confStore = useConferenceStore();
const teachersS = useTeachers();
const teamsS = useTeams();
const parentsS = useParents();

const teachers = computed(() =>
  teachersS.teachers.data
    .filter((i) => !i.deleted)
    .map((i) => ({ title: i.teacher_fname + " " + i.teacher_lname, value: i.uid }))
);
const teams = computed(() =>
  teamsS.teams.data.filter((i) => !i.deleted).map((i) => ({ title: i.team_name, value: i.uid }))
);
const parents = computed(() =>
  parentsS.parents.data
    .filter((i) => !i.deleted)
    .map((i) => ({ title: i.firstname + " " + i.lastname, value: i.uid }))
);

const event = computed(() => confStore.selected!);

const setEvent = async () => {
  await confStore.loadingPromise;
  const event = confStore.conferences.find((c) => c.conference_id === +props.eventId);
  if (event) {
    confStore.selected = cloneDeep(event);
    confStore.selected.conference_days = sort(confStore.selected.conference_days, (i) => i.start);
  }
};
setEvent();

const defVal = {
  table_data: [],
  table_cols: []
};

const { data, loading } = asyncComputed(
  async (controller) => {
    if (!event.value || !state.type) return defVal;

    // Handle entity-specific report types
    if (state.type === "teacher" && !state.teacher) return defVal;
    if (state.type === "team" && !state.team) return defVal;
    if (state.type === "parent" && !state.parent) return defVal;
    
    // Validate report type exists in available report types
    if (!reportTypes.value.find(rt => rt.value === state.type)) {
      console.warn(`Report type '${state.type}' not found in available report types`);
      return defVal;
    }
    let url = `/reports/${state.type}/${props.eventId}`;
    if (["teacher", "team", "parent"].includes(state.type as string)) {
      const entityId = state[state.type];
      url += `?${state.type}=${entityId}`;
      console.log(`Fetching ${state.type} report for ID:`, entityId);
    }
    const res = (await FetchService.fetchData(
      url,
      "get",
      undefined,
      undefined,
      controller
    )) as ReportPayload;
    if (res?.table_cols && res?.table_data) {
      console.log("API Response for report:", { 
        type: state.type,
        id: state[state.type],
        hasTableData: Array.isArray(res.table_data),
        tableDataLength: res.table_data?.length || 0,
        hasConferenceDays: "conference_days" in res,
        conferenceDaysLength: Array.isArray(res.conference_days) ? res.conference_days.length : 0,
        tableData: res.table_data,
        tableColumns: res.table_cols
      });
      
      // Skip the empty timeslot addition for now since it's causing issues
      // Just return the raw data directly from the API
      if (["teacher", "team"].includes(state.type) && "conference_days" in res) {
        try {
          // Only attempt to process if we have valid arrays
          if (Array.isArray(res.table_data) && 
              Array.isArray(res.conference_days) && 
              res.conference_days.length > 0) {
            res.table_data = addEmptyTimeSlot(res.table_data, res.conference_days as any[]);
          } else {
            console.log("Skipping addEmptyTimeSlot due to missing or invalid data");
          }
        } catch (error) {
          console.error("Error in addEmptyTimeSlot:", error);
        }
      }
      return res;
    }
    return defVal;
  },
  [event, () => state.type, () => state.teacher, () => state.parent, () => state.team],
  undefined,
  undefined,
  defVal
);

const { data: reportTypes, loading: loadingReports } = asyncComputed(
  async () => {
    const res = await FetchService.fetchData("/reports", "get");
    if (Array.isArray(res))
      return res as {
        value: string;
        title: string;
      }[];
    return [];
  },
  undefined,
  undefined,
  undefined,
  []
);
const selectedReport = computed(() => reportTypes.value.find((i) => i.value === state.type));

const showTable = computed(() => {
  if (!selectedReport.value) return false;
  if (state.type === "teacher" && !state.teacher) return false;
  if (state.type === "team" && !state.team) return false;
  if (state.type === "parent" && !state.parent) return false;
  return true;
});
watch([() => props.eventId, () => confStore.conferences.length], setEvent);
watch([() => router.currentRoute.value.query.type, reportTypes], () => {
  if (!loadingReports.value) {
    if (reportTypes.value.find((r) => r.value === router.currentRoute.value.query.type))
      state.type = router.currentRoute.value.query.type as any;
    else state.type = "";
  }
});

const getEntityName = () => {
  const type = selectedReport.value?.value;
  if (type == "parent") {
    const p = parentsS.parents.data.find((p) => p.uid == state.parent);
    if (p) return `${p.firstname}, ${p.lastname}`;
    return;
  }
  if (type == "teacher") {
    const t = teachersS.teachers.data.find((t) => t.uid == state.teacher);
    if (t) return `${t.teacher_fname}, ${t.teacher_lname}`;
    return;
  }
  if (type == "team") {
    const t = teamsS.teams.data.find((t) => t.uid == state.team);
    if (t) return `${t.team_name}`;
    return;
  }
  return;
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.reports-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-top: 20px;
  .title {
    font-size: 1.25rem;
    color: gray;
    font-weight: 600;
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    //   width: 100%;
    gap: 20px;
    flex-grow: 1;
    .action {
      color: $primary;
      display: flex;
      flex-direction: column;
      align-items: center;
      cursor: pointer;
      padding: 5px;
      border-radius: 4px;

      &:active {
        outline: 2px solid $primary-75;
      }
      gap: 5px;
      .icon {
        font-size: 1.5rem;
        font-weight: 600;
        flex-grow: 1;
      }
      .text {
        font-weight: 600;
        text-transform: uppercase;
      }
    }
  }
}
</style>
